import {AppContext} from "../app-context";
import {
    AuthenticationResponse,
    AuthenticationResult,
    ConfirmUser,
    ImpersonateUser,
    SetUserPassword,
    SignInUser,
    SignUpUser
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {inject, Injectable} from "@angular/core";
import {Router} from "@angular/router";
import moment, {Moment} from "moment";
import {StandaloneHandler} from "../common/standalone-handler";
import {AppCommonUtils, sendCommand} from "src/app/common/app-common-utils";
import {environment} from "../../environments/environment";
import {HandleEvent} from "../common/handle-event";

export type SessionInvalidationReason =
    "missing-authorization"
    | "missing-session-deadline"
    | "invalid-session-deadline"
    | "expired-session"
    | "unauthorized"
    | "invalid-authentication-response"
    | "authentication-storage-failed"
    | "missing-user-profile"
    | "user-profile-error"
    | "signed-out";

type StoredSessionState =
    {valid: true, deadline: Moment}
    | {valid: false, reason: SessionInvalidationReason};

@Injectable()
export class AuthService extends StandaloneHandler {
    private redirectUrl: string;
    private router: Router = inject(Router);
    private sessionTimer: ReturnType<typeof setTimeout>;
    private navigatingToLogin = false;

    constructor() {
        super();
        this.initialiseStoredSession();
    }

    navigateToLogin() {
        if (this.router.url !== "/login" && !this.navigatingToLogin) {
            this.redirectUrl = this.router.url;
            this.navigatingToLogin = true;
            AppCommonUtils.navigateToUrl("/login");
        }
    }

    @HandleEvent("checkSession")
    checkSession() {
        this.ensureValidSession();
    }

    @HandleEvent("userUnauthorized")
    handleUnauthorized() {
        this.invalidateSession("unauthorized");
    }

    signUp(command: SignUpUser) {
        sendCommand("com.flowmaps.api.authentication.SignUpUser", command, this.responseHandler(command.info.email));
    }

    signIn(command: SignInUser) {
        sendCommand("com.flowmaps.api.authentication.SignInUser", command, this.responseHandler(command.userId));
    }

    impersonate(command: ImpersonateUser) {
        sendCommand("com.flowmaps.api.authentication.ImpersonateUser", command,
            this.responseHandler(command.userId, true));
    }

    stopImpersonating() {
        sendCommand("com.flowmaps.api.authentication.StopImpersonatingUser", {},
            this.responseHandler(null, true));
    }

    signOut() {
        AppCommonUtils.httpClient().get(environment.apiProtocol + environment.apiDomain + "/api/logout", {withCredentials : true}).subscribe();
        this.redirectUrl = null;
        this.navigatingToLogin = true;
        this.invalidateSession("signed-out", false);
        AppCommonUtils.navigateToUrl("/login");
    }

    startSession(response: AuthenticationResponse): boolean {
        if (!this.storeAuthentication(response)) {
            return false;
        }
        AppContext.setUserProfile(response.userProfile);
        return true;
    }

    hasValidSession = (): boolean => this.readStoredSession().valid;

    ensureValidSession = (): boolean => {
        const session = this.readStoredSession();
        if ("reason" in session) {
            this.invalidateSession(session.reason);
            return false;
        }
        return true;
    }

    invalidateSession(reason: SessionInvalidationReason, navigateToLogin = true) {
        this.clearSessionTimer();
        let hadAuthorization = false;
        let hadSessionDeadline = false;
        try {
            hadAuthorization = Boolean(localStorage.getItem("Authorization"));
            hadSessionDeadline = Boolean(localStorage.getItem("SessionDeadline"));
            localStorage.removeItem("Authorization");
            localStorage.removeItem("SessionDeadline");
            localStorage.removeItem("impersonator");
        } catch (error) {
            console.warn("Authentication state could not be cleared from local storage", error);
        }
        if (reason !== "signed-out") {
            console.warn("Authentication session invalidated", {reason, hadAuthorization, hadSessionDeadline});
        }
        AppContext.setUserProfile(undefined);
        if (navigateToLogin) {
            this.navigateToLogin();
        }
    }

    private storeAuthentication(response: AuthenticationResponse): boolean {
        const sessionDeadline = response.sessionDeadline && this.parseSessionDeadline(response.sessionDeadline);
        if (!response.authorizationHeader || !sessionDeadline || !sessionDeadline.isAfter(moment()) || !response.userProfile) {
            this.invalidateSession("invalid-authentication-response");
            return false;
        }

        try {
            localStorage.setItem("Authorization", response.authorizationHeader);
            localStorage.setItem("SessionDeadline", response.sessionDeadline);
            if (response.impersonator) {
                localStorage.setItem("impersonator", response.impersonator);
            } else {
                localStorage.removeItem("impersonator");
            }
            if (localStorage.getItem("Authorization") !== response.authorizationHeader
                || localStorage.getItem("SessionDeadline") !== response.sessionDeadline) {
                throw new Error("Authentication state could not be read back from local storage");
            }
        } catch (error) {
            console.warn("Authentication state could not be stored", error);
            this.invalidateSession("authentication-storage-failed");
            return false;
        }

        this.navigatingToLogin = false;
        AppCommonUtils.clearCache();
        this.scheduleSessionExpiry(sessionDeadline);
        return true;
    }

    private responseHandler = (email? : string, reloadPage = false) => (response: AuthenticationResponse) => {
        if (response.result !== AuthenticationResult.SUCCESS
            && response.result !== AuthenticationResult.NOT_CONFIRMED) {
            return;
        }
        if (!this.startSession(response)) {
            return;
        }
        if (response.result === AuthenticationResult.SUCCESS) {
            AppCommonUtils.navigateToUrl(this.redirectUrl || "", reloadPage);
            this.redirectUrl = null;
        }
        if (response.result === AuthenticationResult.NOT_CONFIRMED) {
            AppCommonUtils.navigateToUrl((this.redirectUrl || "").startsWith("/confirm")
                ? this.redirectUrl : "/confirm?userId=" + encodeURIComponent(email), reloadPage);
        }
    };

    confirm(command: ConfirmUser) {
        sendCommand('com.flowmaps.api.authentication.ConfirmUser', command, () => {
            AppCommonUtils.navigateToUrl("/");
            AppCommonUtils.registerSuccess("You've successfully confirmed your e-mail address");
        })
    }

    resetPassword(command: SetUserPassword) {
        sendCommand('com.flowmaps.api.authentication.SetUserPassword', command, this.responseHandler(command.userId))
    }

    private initialiseStoredSession() {
        try {
            if (!localStorage.getItem("Authorization")
                && !localStorage.getItem("SessionDeadline")
                && !localStorage.getItem("impersonator")) {
                return;
            }
        } catch (error) {
            console.warn("Authentication state could not be read from local storage", error);
            this.invalidateSession("authentication-storage-failed");
            return;
        }
        const session = this.readStoredSession();
        if ("reason" in session) {
            this.invalidateSession(session.reason);
            return;
        }
        this.scheduleSessionExpiry(session.deadline);
    }

    private readStoredSession(): StoredSessionState {
        try {
            const authorization = localStorage.getItem("Authorization");
            if (!authorization) {
                return {valid: false, reason: "missing-authorization"};
            }
            const storedDeadline = localStorage.getItem("SessionDeadline");
            if (!storedDeadline) {
                return {valid: false, reason: "missing-session-deadline"};
            }
            const deadline = this.parseSessionDeadline(storedDeadline);
            if (!deadline) {
                return {valid: false, reason: "invalid-session-deadline"};
            }
            if (!deadline.isAfter(moment())) {
                return {valid: false, reason: "expired-session"};
            }
            return {valid: true, deadline};
        } catch (error) {
            console.warn("Authentication state could not be read from local storage", error);
            return {valid: false, reason: "authentication-storage-failed"};
        }
    }

    private parseSessionDeadline(sessionDeadline: string): Moment {
        const parsedDeadline = moment(sessionDeadline, moment.ISO_8601, true);
        return parsedDeadline.isValid() ? parsedDeadline : null;
    }

    private scheduleSessionExpiry(sessionDeadline: Moment) {
        this.clearSessionTimer();
        this.sessionTimer = setTimeout(
            () => this.checkSessionExpiry(),
            sessionDeadline.diff(moment())
        );
    }

    private checkSessionExpiry() {
        this.sessionTimer = null;
        const session = this.readStoredSession();
        if ("reason" in session) {
            this.invalidateSession(session.reason);
            return;
        }
        this.scheduleSessionExpiry(session.deadline);
    }

    private clearSessionTimer() {
        if (this.sessionTimer != null) {
            clearTimeout(this.sessionTimer);
            this.sessionTimer = null;
        }
    }
}
